import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { FC, useEffect, useState } from "react";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { useAttachServices, useDetachServices } from "../../../api/signs";
import { useGetAllServices } from "../../../api/service";
import useStyles from "./styles";
import { CustomCheckBoxService } from "../../../components/CustomCheckBoxService/CustomCheckBoxService";

type ModalManageServicesProps = {
  open: boolean;
  signId: number;
  services: {
    id: number;
    name: string;
    service_code: string | null;
    pivot: {
      sign_id: number;
      service_id: number;
    } | null;
  }[];
  onCancel?: () => void;
  onClose?: () => void;
};

export const ModalManageServices: FC<ModalManageServicesProps> = ({
  open,
  signId,
  services,
  onCancel,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  
  // Fetch all available services
  const { services: allServices, isPending: loadingServices } = useGetAllServices();
  
  // Track selected services
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  
  // Currently attached services (this represents the original state)
  const [attachedServiceIds, setAttachedServiceIds] = useState<number[]>([]);
  
  // Hooks for attaching and detaching services
  const { mutate: attachServices, isPending: isAttaching } = useAttachServices(signId);
  const { mutate: detachServices, isPending: isDetaching } = useDetachServices(signId);

  // Initialize attached services from props and set them as selected
  useEffect(() => {
    if (open && services) {
      const attachedIds = services.map(service => service.id);
      console.log('Initializing modal with attached services:', attachedIds);
      setAttachedServiceIds(attachedIds);
      // Initialize selected services with currently attached services
      setSelectedServices([...attachedIds]); // Create a new array copy
    }
  }, [services, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedServices([]);
      setAttachedServiceIds([]);
    }
  }, [open]);

  const handleToggleService = (serviceId: number) => {
    console.log('Toggling service:', serviceId);
    console.log('Current selected services before toggle:', selectedServices);
    
    setSelectedServices(prev => {
      let newSelected;
      if (prev.includes(serviceId)) {
        newSelected = prev.filter(id => id !== serviceId);
      } else {
        newSelected = [...prev, serviceId];
      }
      console.log('New selected services after toggle:', newSelected);
      return newSelected;
    });
  };

  const handleSave = () => {
    console.log('=== SAVE OPERATION ===');
    console.log('Selected services:', selectedServices);
    console.log('Originally attached services:', attachedServiceIds);
    
    // Simple approach: just send ALL selected services to attach API
    // This assumes your API will handle replacing the existing services
    
    if (selectedServices.length > 0) {
      console.log('Sending all selected services to attach API:', selectedServices);
      attachServices(selectedServices, {
        onSuccess: () => {
          console.log('Successfully attached all services');
          onClose && onClose();
        },
        onError: (error) => {
          console.error('Error attaching services:', error);
        }
      });
    } else {
      // If no services selected, detach all current services
      if (attachedServiceIds.length > 0) {
        console.log('No services selected, detaching all:', attachedServiceIds);
        detachServices(attachedServiceIds, {
          onSuccess: () => {
            console.log('Successfully detached all services');
            onClose && onClose();
          },
          onError: (error) => {
            console.error('Error detaching services:', error);
          }
        });
      } else {
        console.log('No changes needed, closing modal');
        onClose && onClose();
      }
    }
  };

  // Helper function to check if service is currently attached (originally)
  const isServiceAttached = (serviceId: number): boolean => {
    return attachedServiceIds.includes(serviceId);
  };

  // Helper function to check if service selection has changed
  const hasChanges = (): boolean => {
    const servicesToAttach = selectedServices.filter(id => !attachedServiceIds.includes(id));
    const servicesToDetach = attachedServiceIds.filter(id => !selectedServices.includes(id));
    return servicesToAttach.length > 0 || servicesToDetach.length > 0;
  };

  /*
   * Appointment booking ("prise de RDV") is offered by the mobile app only when
   * a sign has a centre_code AND at least one attached service carrying a
   * service_code. Services without a service_code exist purely as descriptive
   * tags — ticking them will never make a centre bookable.
   *
   * Nothing in this modal used to say so. An admin could tick "Freinage",
   * "Moteur" or any other code-less service, get a success toast, and be left
   * wondering why RDV was still off. The banner and the per-row badge below
   * make the distinction explicit.
   */
  const bookableSelectedCount = selectedServices.filter(id =>
    allServices?.some(s => s.id === id && !!s.service_code)
  ).length;

  return (
    <CustomModal
      open={open}
      onCancel={onCancel}
      title={t("sign.manageServices", "Manage Services")}
      onClose={onClose}
      onSave={handleSave}
      loadingSave={isAttaching || isDetaching}
      // Disable save button if no changes
      saveDisabled={!hasChanges()}
    >
      <div className={classes.servicesContainer}>
        {loadingServices ? (
          <CustomTypography 
            text={t("common.loading", "Loading...")} 
            variant="default" 
          />
        ) : (
          <>
            <CustomTypography 
              text={t("sign.selectServices", "Select services for this sign")} 
              variant="default" 
            />

            {/* Booking eligibility — the whole point admins come to this modal. */}
            <div
              className={
                bookableSelectedCount > 0
                  ? classes.rdvBannerOk
                  : classes.rdvBannerWarn
              }
            >
              {bookableSelectedCount > 0
                ? t(
                    "sign.rdvEnabled",
                    "Prise de RDV activée : {{count}} service(s) réservable(s) sélectionné(s). Le centre doit aussi avoir un code centre renseigné.",
                    { count: bookableSelectedCount }
                  )
                : t(
                    "sign.rdvDisabled",
                    "Prise de RDV désactivée : aucun service réservable sélectionné. Seuls les services marqués « Réservable » (ceux qui portent un code service) activent la prise de RDV."
                  )}
            </div>
            
            {/* Display count of currently attached services */}
            {attachedServiceIds.length > 0 && (
              <div className={classes.attachedServicesInfo}>
                <CustomTypography 
                  text={t("sign.currentlyAttached", { count: attachedServiceIds.length })} 
                  variant="default" 
                />
              </div>
            )}
            
            {allServices?.length === 0 ? (
              <CustomTypography 
                text={t("sign.noServices", "No services available")} 
                variant="default" 
              />
            ) : (
              <div className={classes.servicesList}>
                {allServices?.map(service => {
                  const isAttached = isServiceAttached(service.id);
                  const isSelected = selectedServices.includes(service.id);
                  
                  return (
                    <div key={service.id} className={classes.serviceItem}>
                      <CustomCheckBoxService
                        checked={isSelected}
                        onChange={() => handleToggleService(service.id)}
                        label={
                          <div className={classes.serviceLabelContainer}>
                            <span>{service.name}</span>
                            {isAttached && (
                              <span className={classes.attachedBadge}>
                                {t("sign.attached", "Attached")}
                              </span>
                            )}
                            {service.service_code ? (
                              <>
                                <span className={classes.bookableBadge}>
                                  {t("sign.bookable", "Réservable")}
                                </span>
                                <span className={classes.serviceCode}>
                                  ({service.service_code})
                                </span>
                              </>
                            ) : (
                              <span
                                className={classes.notBookableBadge}
                                title={t(
                                  "sign.notBookableHint",
                                  "Ce service n'a pas de code service, il n'active pas la prise de RDV."
                                )}
                              >
                                {t("sign.notBookable", "Non réservable")}
                              </span>
                            )}
                          </div>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Display changes summary */}
            {hasChanges() && (
              <div className={classes.changesSummary}>
                <CustomTypography 
                  text={t("sign.changesSummary", "Changes:")} 
                  variant="default" 
                />
                {selectedServices.filter(id => !attachedServiceIds.includes(id)).length > 0 && (
                  <CustomTypography 
                    text={t("sign.toAttach", { count: selectedServices.filter(id => !attachedServiceIds.includes(id)).length })}
                    variant="default" 
                  />
                )}
                {attachedServiceIds.filter(id => !selectedServices.includes(id)).length > 0 && (
                  <CustomTypography 
                    text={t("sign.toDetach", { count: attachedServiceIds.filter(id => !selectedServices.includes(id)).length })}
                    variant="default" 
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </CustomModal>
  );
};